import { ISaveInstallationGroup } from "../core/contracts/ISaveInstallationGroup";
import InstallationGroup, { EnrichedInstallationGroup } from "../core/models/InstallationGroup";
import { IEnumerable } from "../core/models/ModelHelpers";
import BaseService from "./BaseService";
import sortBy from "lodash/sortBy";
import { SessionCache } from "../helpers/cache";
import CustomerModel from "../core/models/Customer";
import includes from "lodash/includes";
import without from "lodash//without";
import InstallationService from "./InstallationService";
import { isArrayValid } from "../helpers/utility";
import { IInstallation } from "../core/contracts/IInstallation";
import UserService from "./UserService";

export default class InstallationGroupService extends BaseService {
  async fetchAll(): Promise<EnrichedInstallationGroup[]> {
    try {
      const APIResponse: InstallationGroup[] = await new BaseService(true).get<InstallationGroup[]>(
        "/core/InstallationGroup"
      );

      let ret: EnrichedInstallationGroup[] = APIResponse;
      if (isArrayValid(APIResponse)) {
        const installationsToFetch = APIResponse.flatMap((x) =>
          x.installationList.map((y) => y.installationId)
        ).filter((x) => x) as IEnumerable[];
        const installationsFromAPI: IInstallation[] = await new InstallationService().fetchMultiple(
          installationsToFetch
        );

        ret = APIResponse.map((x) => {
          const installationsIds = x.installationList.map((y) => y.installationId);
          return {
            ...x,
            installations: installationsFromAPI.filter((installation) =>
              installationsIds.includes(installation.installation_id)
            ),
          };
        });
      }

      return ret;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetchAll -> error", error);
      return Promise.reject(error);
    }
  }

  async fetch(id: IEnumerable): Promise<InstallationGroup | EnrichedInstallationGroup> {
    const cache = new SessionCache();

    try {
      const APIResponse: InstallationGroup = await new BaseService(true).get<InstallationGroup>(
        `/core/InstallationGroup/${id}`
      );

      const installationsIds = APIResponse?.installationList?.map((x) => x.installationId) ?? [];

      const cachedCustomers: CustomerModel[] | null = cache.get("customers");
      let installationsToReturn: IInstallation[] = [];
      if (isArrayValid(cachedCustomers)) {
        const cachedInstallations = cachedCustomers?.flatMap((x) => x.installations) ?? [];
        const cachedInstallationsIds =
          (cachedInstallations?.map((x) => x?.installation_id)?.filter((x) => x) as string[]) ?? [];

        installationsToReturn =
          (cachedInstallations?.filter((x) =>
            includes(installationsIds, x?.installation_id)
          ) as IInstallation[]) ?? [];
        const installationsToFetch = without(
          installationsIds,
          ...cachedInstallationsIds
        ) as IEnumerable[];

        if (isArrayValid(installationsToFetch)) {
          try {
            const installationsFromAPI: IInstallation[] = await new InstallationService().fetchMultiple(
              installationsToFetch
            );
            if (isArrayValid(installationsFromAPI)) {
              installationsToReturn = sortBy(
                [...installationsToReturn, ...installationsFromAPI],
                "installation_id"
              ) as IInstallation[];
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log("InstallationGroupService -> fetch → error", error);
            return Promise.reject(error);
          }
        }
      }

      const sortedResponse: EnrichedInstallationGroup = {
        ...APIResponse,
        installationList: sortBy(APIResponse.installationList, "installationId"),
        userList: sortBy(APIResponse.userList, "email"),
        permissionList: sortBy(APIResponse.permissionList, "type"),
        installations: installationsToReturn,
      };

      return sortedResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetch -> error", error);
      return Promise.reject(error);
    }
  }

  async remove(id: IEnumerable): Promise<boolean> {
    try {
      const action = await new BaseService(true).delete<boolean, { id: IEnumerable }>(
        "/core/InstallationGroup",
        {
          id: id,
        }
      );
      // update user after making changes to groups
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> remove -> error", error);
      return Promise.reject(error);
    }
  }

  async update(params: ISaveInstallationGroup): Promise<boolean> {
    try {
      const action = await new BaseService(true).put<boolean, ISaveInstallationGroup>(
        "/core/InstallationGroup",
        params
      );
      // update user after making changes to groups
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> update -> error", error);
      return Promise.reject(error);
    }
  }

  async create(params: ISaveInstallationGroup): Promise<boolean> {
    try {
      const action = await new BaseService(true).post<boolean, ISaveInstallationGroup>(
        "/core/InstallationGroup",
        params
      );
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> create -> error", error);
      return Promise.reject(error);
    }
  }
}
